import { useEffect, useState } from "react";
import { DashboardPage } from "./design-system/pages/DashboardPage";
import { ClientsListPage } from "./design-system/pages/ClientsListPage";
import { AmbassadorWorkspacePage } from "./design-system/pages/AmbassadorWorkspacePage";
import { PilotWorkspacePage } from "./design-system/pages/PilotWorkspacePage";
import { AdminUsersPage } from "./design-system/pages/AdminUsersPage";
import { AdminOrganizationsPage } from "./design-system/pages/AdminOrganizationsPage";
import { AdminPromotionsPage } from "./design-system/pages/AdminPromotionsPage";
import { CatalogPage } from "./design-system/pages/CatalogPage";
import { InventoryModulePage } from "./design-system/pages/inventory/InventoryModulePage";
import { ProcessBuilderPage } from "./design-system/pages/ProcessBuilderPage";
import { StudioLandingPage } from "./design-system/pages/studio/StudioLandingPage";
import { StudioRuleSetsPage } from "./design-system/pages/studio/StudioRuleSetsPage";
import { StudioRuleSetDetailPage } from "./design-system/pages/studio/StudioRuleSetDetailPage";
import { StudioProcessTemplatesPage } from "./design-system/pages/studio/StudioProcessTemplatesPage";
import { StudioProcessTemplateEditorPage } from "./design-system/pages/studio/StudioProcessTemplateEditorPage";
import { StudioHandlersPage } from "./design-system/pages/studio/StudioHandlersPage";
import { StudioPublicationsPage } from "./design-system/pages/studio/StudioPublicationsPage";
import { AdminEmailTemplatesPage } from "./design-system/pages/AdminEmailTemplatesPage";
import { EmailSendPage } from "./design-system/pages/EmailSendPage";
import { BookingsPage } from "./design-system/pages/BookingsPage";
import { PublicBookingPage } from "./design-system/pages/PublicBookingPage";
import { QuoteBuilderPage } from "./design-system/pages/QuoteBuilderPage";
import { TasksPage } from "./design-system/pages/TasksPage";
import { EngagementListPage } from "./design-system/pages/ops/EngagementListPage";
import { EngagementDetailPage } from "./design-system/pages/ops/EngagementDetailPage";
import { ProcessCaseDetailPage } from "./design-system/pages/ops/ProcessCaseDetailPage";
import { OpsLandingPage } from "./design-system/pages/ops/OpsLandingPage";
import { OpsDashboardPage } from "./design-system/pages/ops/OpsDashboardPage";
import { MyWorkPage } from "./design-system/pages/ops/MyWorkPage";
import { LoginPage } from "./design-system/pages/LoginPage";
import { SetPasswordPage } from "./design-system/pages/SetPasswordPage";
import { ResetPasswordPage } from "./design-system/pages/ResetPasswordPage";
import { UnsubscribePage } from "./design-system/pages/UnsubscribePage";
import { PresentationXlkeySurveyPage } from "./design-system/pages/PresentationXlkeySurveyPage";
import { ArlaAerialProductsPage } from "./design-system/pages/ArlaAerialProductsPage";
import { ProfilePage } from "./design-system/pages/ProfilePage";
import { getAdminOrgId } from "./api/admin";
import {
  acceptInvitation,
  clearAccessToken,
  getAccessToken,
  getMe,
  loginLocal,
  type AuthUser,
} from "./api/auth";
import { setMembershipsWithPermissions } from "./state/orgContext";
import {
  type View,
  hashToView,
  viewToHash,
  getSetPasswordTokenFromHash,
  getResetPasswordTokenFromHash,
  getOpsEngagementIdFromHash,
  getOpsProcessCaseIdFromHash,
  getStudioRuleSetIdFromHash,
  getStudioProductIdFromHash,
  getSoumissionQueryFromHash,
  getStudioProcessTemplateEditorIdFromHash,
  getPublicBookingTokenFromHash,
} from "./routing";

// Re-export routing helpers for any code that still imports them from App
export {
  getOpsEngagementIdFromHash,
  getOpsProcessCaseIdFromHash,
  getStudioRuleSetIdFromHash,
  getStudioProductIdFromHash,
  getSoumissionQueryFromHash,
  getStudioProcessTemplateEditorIdFromHash,
  getPublicBookingTokenFromHash,
} from "./routing";

export function App() {
  const inviteToken = getSetPasswordTokenFromHash();
  const resetToken = getResetPasswordTokenFromHash();

  const [view, setView] = useState<View>(() => hashToView(window.location.hash));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [authError, setAuthError] = useState<"invalid" | "server_error" | null>(null);

  const checkSession = () => {
    const token = getAccessToken();
    if (!token) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAuthError(null);
      setAuthReady(true);
      return;
    }
    setAuthError(null);
    getMe()
      .then((me) => {
        setCurrentUser(me.user);
        setMembershipsWithPermissions(me.memberships_with_permissions ?? []);
        setIsAuthenticated(true);
        setAuthError(null);
      })
      .catch((error: any) => {
        setCurrentUser(null);
        const status = error.status;
        const invalidSession = status === 401 || error.code === "account_deleted" || error.code === "account_disabled";
        if (invalidSession) {
          clearAccessToken();
          setIsAuthenticated(false);
          setAuthError("invalid");
          if (error.code === "account_deleted" || error.code === "account_disabled") {
            setView("dashboard");
          }
        } else {
          // 5xx or network: keep token, show "service unavailable" so refresh doesn't "logout"
          setIsAuthenticated(false);
          setAuthError("server_error");
        }
      })
      .finally(() => setAuthReady(true));
  };

  useEffect(() => {
    checkSession();
  }, []);

  // Preload admin org ID immediately on mount to cache it before any page loads
  useEffect(() => {
    if (!isAuthenticated) return;
    // Fire and forget - this will cache the org ID for all subsequent requests
    getAdminOrgId().catch(() => {
      // Silently fail - will be retried when needed
    });
  }, [isAuthenticated]);

  useEffect(() => {
    const syncViewFromHash = () => setView(hashToView(window.location.hash));
    syncViewFromHash();
    window.addEventListener("hashchange", syncViewFromHash);
    return () => window.removeEventListener("hashchange", syncViewFromHash);
  }, []);

  useEffect(() => {
    if (!authReady) return;
    setView(hashToView(window.location.hash));
  }, [authReady]);

  useEffect(() => {
    const handleUserMenu = (event: Event) => {
      const customEvent = event as CustomEvent<{ action?: string }>;
      const action = customEvent.detail?.action;
      if (action === "logout") {
        handleLogout();
      }
    };
    const handleSessionExpired = () => {
      clearAccessToken();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAuthError("invalid");
      setView("dashboard");
    };
    window.addEventListener("xlflow:user-menu", handleUserMenu as EventListener);
    window.addEventListener("xlflow:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("xlflow:user-menu", handleUserMenu as EventListener);
      window.removeEventListener("xlflow:session-expired", handleSessionExpired);
    };
  }, []);

  const changeView = (next: View) => {
    window.location.hash = viewToHash(next);
    setView(next);
  };

  const handleLogin = async (email: string, password: string) => {
    const user = await loginLocal(email, password);
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearAccessToken();
    setCurrentUser(null);
    setIsAuthenticated(false);
    window.location.hash = "#/";
    setView("dashboard");
  };

  const handleAcceptInvitation = async (
    password: string,
    confirmPassword: string,
    phone: string,
    address_line1?: string | null,
    address_line2?: string | null,
    city?: string | null,
    postal_code?: string | null,
    province?: string | null,
    country?: string | null
  ) => {
    const user = await acceptInvitation(
      inviteToken,
      password,
      confirmPassword,
      phone,
      address_line1,
      address_line2,
      city,
      postal_code,
      province,
      country
    );
    setCurrentUser(user);
    setIsAuthenticated(true);
    // Clear the invite token from URL
    window.location.hash = "#/";
    setView("dashboard");
    // Force a page reload to ensure all components refresh with new user data
    window.location.reload();
  };

  const isSuperAdmin = Boolean(currentUser?.is_superadmin);
  const currentUserName =
    currentUser?.display_name?.trim() ||
    `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`.trim() ||
    currentUser?.email ||
    "Utilisateur";

  let currentPage: JSX.Element;
  if (!authReady) {
    currentPage = <div className="p-6 text-sm text-muted">Chargement...</div>;
  } else if (view === "set-password") {
    currentPage = <SetPasswordPage token={inviteToken} onSubmit={handleAcceptInvitation} />;
  } else if (view === "reset-password") {
    currentPage = <ResetPasswordPage token={resetToken} />;
  } else if (view === "unsubscribe") {
    currentPage = <UnsubscribePage />;
  } else if (view === "public-booking") {
    currentPage = <PublicBookingPage token={getPublicBookingTokenFromHash()} />;
  } else if (view === "survey-presentation-xlkey") {
    currentPage = <PresentationXlkeySurveyPage />;
  } else if (view === "arla-aerial-products") {
    currentPage = <ArlaAerialProductsPage />;
  } else if (!isAuthenticated && authError === "server_error") {
    currentPage = (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-surface text-text">
        <h1 className="text-xl font-semibold">Service temporairement indisponible</h1>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Impossible de vérifier votre session. Le serveur ne répond pas. Réessayez dans quelques instants.
        </p>
        <button
          type="button"
          onClick={() => {
            setAuthReady(false);
            setAuthError(null);
            checkSession();
          }}
          className="px-4 py-2 rounded-md bg-accent text-white hover:bg-accent-hover"
        >
          Réessayer
        </button>
      </div>
    );
  } else if (!isAuthenticated) {
    currentPage = <LoginPage onSubmit={handleLogin} sessionExpired={authError === "invalid"} />;
  } else if (view === "clients") {
    currentPage = <ClientsListPage userName={currentUserName} />;
  } else if (view === "soumission") {
    const { clientId, clientName } = getSoumissionQueryFromHash();
    currentPage = (
      <QuoteBuilderPage
        userName={currentUserName}
        preselectedClientId={clientId}
        preselectedClientName={clientName}
      />
    );
  } else if (view === "ambassadors") {
    currentPage = <AmbassadorWorkspacePage userName={currentUserName} />;
  } else if (view === "pilots") {
    currentPage = <PilotWorkspacePage userName={currentUserName} />;
  } else if (view === "ops-landing") {
    currentPage = (
      <OpsLandingPage
        userName={currentUserName}
        onBackToDashboard={() => changeView("dashboard")}
        onOpenDashboard={() => { window.location.hash = "#/ops/dashboard"; }}
        onOpenDossiers={() => { window.location.hash = "#/ops/engagements"; }}
        onOpenMyWork={() => { window.location.hash = "#/ops/my-work"; }}
      />
    );
  } else if (view === "ops-dashboard") {
    currentPage = (
      <OpsDashboardPage
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/ops"; }}
        onOpenEngagements={() => { window.location.hash = "#/ops/engagements"; }}
        onOpenMyWork={() => { window.location.hash = "#/ops/my-work"; }}
      />
    );
  } else if (view === "ops-my-work") {
    currentPage = (
      <MyWorkPage
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/ops"; }}
        onOpenProcessCase={(eid, pid, activityId) => {
          const hash = activityId
            ? `#/ops/engagements/${eid}/process-cases/${pid}?activityId=${activityId}`
            : `#/ops/engagements/${eid}/process-cases/${pid}`;
          window.location.hash = hash;
        }}
      />
    );
  } else if (view === "ops-engagements") {
    currentPage = (
      <EngagementListPage
        userName={currentUserName}
        onBackToDashboard={() => changeView("dashboard")}
        onOpenEngagement={(id) => {
          window.location.hash = `#/ops/engagements/${id}`;
        }}
      />
    );
  } else if (view === "ops-engagement-detail") {
    const engagementId = getOpsEngagementIdFromHash();
    currentPage =
      engagementId ? (
        <EngagementDetailPage
          engagementId={engagementId}
          userName={currentUserName}
          onBackToList={() => {
            window.location.hash = "#/ops/engagements";
          }}
          onOpenProcessCase={(eid, pid) => {
            window.location.hash = `#/ops/engagements/${eid}/process-cases/${pid}`;
          }}
        />
      ) : (
        <div className="p-6 text-sm text-muted">ID de dossier manquant.</div>
      );
  } else if (view === "ops-process-case-detail") {
    const engagementId = getOpsEngagementIdFromHash();
    const processCaseId = getOpsProcessCaseIdFromHash();
    currentPage =
      engagementId && processCaseId ? (
        <ProcessCaseDetailPage
          engagementId={engagementId}
          processCaseId={processCaseId}
          userName={currentUserName}
          onBackToEngagement={() => {
            window.location.hash = `#/ops/engagements/${engagementId}`;
          }}
        />
      ) : (
        <div className="p-6 text-sm text-muted">ID de dossier ou de processus manquant.</div>
      );
  } else if (view === "inventories") {
    currentPage = <InventoryModulePage userName={currentUserName} />;
  } else if (view === "tasks") {
    currentPage = <TasksPage userName={currentUserName} />;
  } else if (view === "admin-users") {
    currentPage = <AdminUsersPage userName={currentUserName} isSuperAdmin={isSuperAdmin} />;
  } else if (view === "admin-orgs") {
    currentPage = <AdminOrganizationsPage userName={currentUserName} />;
  } else if (view === "admin-products") {
    currentPage = <CatalogPage userName={currentUserName} />;
  } else if (view === "admin-promotions") {
    currentPage = <AdminPromotionsPage userName={currentUserName} />;
  } else if (view === "admin-processes") {
    currentPage = <ProcessBuilderPage userName={currentUserName} />;
  } else if (view === "admin-billing") {
    currentPage = <div className="p-6">Facturation (à implémenter)</div>;
  } else if (view === "admin-email-templates") {
    currentPage = <AdminEmailTemplatesPage userName={currentUserName} />;
  } else if (view === "email-send") {
    currentPage = <EmailSendPage userName={currentUserName} />;
  } else if (view === "bookings") {
    currentPage = (
      <BookingsPage userName={currentUserName} currentUserId={currentUser?.id ?? ""} />
    );
  } else if (view === "profile") {
    currentPage = <ProfilePage userName={currentUserName} />;
  } else if (view === "settings") {
    currentPage = (
      <div className="p-6">
        <h2 className="text-lg font-semibold">Paramètres</h2>
        <p className="mt-2 text-sm text-muted">La section paramètres sera enrichie dans une prochaine itération.</p>
      </div>
    );
  } else if (view === "studio") {
    currentPage = (
      <StudioLandingPage
        userName={currentUserName}
        onBackToDashboard={() => changeView("dashboard")}
        onOpenRuleSets={() => { window.location.hash = "#/studio/rule-sets"; }}
        onOpenProcessTemplates={() => { window.location.hash = "#/studio/process-templates"; }}
        onOpenHandlers={() => { window.location.hash = "#/studio/handlers"; }}
        onOpenPublications={() => { window.location.hash = "#/studio/publications"; }}
      />
    );
  } else if (view === "studio-rule-sets") {
    currentPage = (
      <StudioRuleSetsPage
        userName={currentUserName}
        productIdFromQuery={getStudioProductIdFromHash()}
        onBack={() => { window.location.hash = "#/studio"; }}
        onOpenRuleSet={(id) => { window.location.hash = `#/studio/rule-sets/${id}${getStudioProductIdFromHash() ? `?product_id=${getStudioProductIdFromHash()}` : ""}`; }}
      />
    );
  } else if (view === "studio-rule-set-detail") {
    const ruleSetId = getStudioRuleSetIdFromHash();
    const productId = getStudioProductIdFromHash();
    currentPage = ruleSetId ? (
      <StudioRuleSetDetailPage
        ruleSetId={ruleSetId}
        productId={productId}
        userName={currentUserName}
        onBack={() => { window.location.hash = productId ? `#/studio/rule-sets?product_id=${productId}` : "#/studio/rule-sets"; }}
      />
    ) : (
      <div className="p-6 text-sm text-muted">ID du Rule Set manquant.</div>
    );
  } else if (view === "studio-process-templates") {
    currentPage = (
      <StudioProcessTemplatesPage
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/studio"; }}
      />
    );
  } else if (view === "studio-process-template-editor") {
    const templateId = getStudioProcessTemplateEditorIdFromHash();
    currentPage = (
      <StudioProcessTemplateEditorPage
        templateId={templateId}
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/studio/process-templates"; }}
      />
    );
  } else if (view === "studio-handlers") {
    currentPage = (
      <StudioHandlersPage
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/studio"; }}
      />
    );
  } else if (view === "studio-publications") {
    currentPage = (
      <StudioPublicationsPage
        userName={currentUserName}
        onBack={() => { window.location.hash = "#/studio"; }}
      />
    );
  } else {
    currentPage = (
      <DashboardPage
        userName={currentUserName}
        isSuperAdmin={isSuperAdmin}
        onOpenClients={() => changeView("clients")}
        onOpenAmbassadors={() => changeView("ambassadors")}
        onOpenPilots={() => changeView("pilots")}
        onOpenKpis={() => changeView("dashboard")}
        onOpenSettings={() => changeView("dashboard")}
        onOpenOperations={() => changeView("operations")}
        onOpenInventories={() => changeView("inventories")}
        onOpenAdminUsers={() => changeView("admin-users")}
        onOpenAdminProducts={() => changeView("admin-products")}
        onOpenAdminPromotions={() => changeView("admin-promotions")}
        onOpenAdminProcesses={() => changeView("admin-processes")}
        onOpenAdminBilling={() => changeView("admin-billing")}
        onOpenAdminEmailTemplates={() => changeView("admin-email-templates")}
        onOpenEmailSend={() => changeView("email-send")}
        onOpenBookings={() => changeView("bookings")}
        onOpenStudio={() => { window.location.hash = "#/studio"; }}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div data-theme="light" className="min-h-screen w-full">{currentPage}</div>
  );
}
